const SCROLL_ANIMATION_TRIGGER_CLASSNAME = "scroll-trigger";
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = "scroll-trigger--offscreen";
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = "animate--zoom-in";
const SCROLL_ANIMATION_CANCEL_CLASSNAME = "scroll-trigger--cancel";

// Scroll in animation logic
function onIntersection(elements, observer) {
	elements.forEach((element, index) => {
		if (element.isIntersecting) {
			const elementTarget = element.target;
			if (
				elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)
			) {
				elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
				if (elementTarget.hasAttribute("data-cascade"))
					elementTarget.setAttribute("style", `--animation-order: ${index};`);
			}
			observer.unobserve(elementTarget);
		} else {
			element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
			element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
		}
	});
}

function initializeScrollAnimationTrigger(
	rootEl = document,
	isDesignModeEvent = false
) {
	const animationTriggerElements = Array.from(
		rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME)
	);
	if (animationTriggerElements.length === 0) return;

	if (isDesignModeEvent) {
		animationTriggerElements.forEach((element) => {
			element.classList.add("scroll-trigger--design-mode");
		});
		return;
	}

	const observer = new IntersectionObserver(onIntersection, {
		rootMargin: "0px 0px -50px 0px",
	});
	animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

	const animationTriggerElements = Array.from(
		document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME)
	);

	if (animationTriggerElements.length === 0) return;

	const scaleAmount = 0.2 / 100;

	animationTriggerElements.forEach((element) => {
		let elementIsVisible = false;
		const observer = new IntersectionObserver((elements) => {
			elements.forEach((entry) => {
				elementIsVisible = entry.isIntersecting;
			});
		});
		observer.observe(element);

		element.style.setProperty(
			"--zoom-in-ratio",
			1 + scaleAmount * percentageSeen(element)
		);

		window.addEventListener(
			"scroll",
			throttle(() => {
				if (!elementIsVisible) return;

				element.style.setProperty(
					"--zoom-in-ratio",
					1 + scaleAmount * percentageSeen(element)
				);
			}),
			{ passive: true }
		);
	});
}

function percentageSeen(element) {
	const viewportHeight = window.innerHeight;
	const scrollY = window.scrollY;
	const elementPositionY = element.getBoundingClientRect().top + scrollY;
	const elementHeight = element.offsetHeight;

	if (elementPositionY > scrollY + viewportHeight) {
		// If we haven't reached the image yet
		return 0;
	} else if (elementPositionY + elementHeight < scrollY) {
		// If we've completely scrolled past the image
		return 100;
	}

	// When the image is in the viewport
	const distance = scrollY + viewportHeight - elementPositionY;
	let percentage = distance / ((viewportHeight + elementHeight) / 100);
	return Math.round(percentage);
}

window.addEventListener("DOMContentLoaded", () => {
	initializeScrollAnimationTrigger();
	initializeScrollZoomAnimationTrigger();
	initializeSVGAnimations();
});

if (Shopify.designMode) {
	document.addEventListener("shopify:section:load", (event) =>
		initializeScrollAnimationTrigger(event.target, true)
	);
	document.addEventListener("shopify:section:reorder", () =>
		initializeScrollAnimationTrigger(document, true)
	);
}

// Throttle function to limit execution rate
function throttle(func, limit) {
	let inThrottle;
	return function (...args) {
		if (!inThrottle) {
			func.apply(this, args);
			inThrottle = true;
			setTimeout(() => (inThrottle = false), limit);
		}
	};
}

//Viewport - Improved detection with margin and horizontal scroll support
function isInViewport(el, margin = 0) {
	const rect = el.getBoundingClientRect();
	const windowWidth = window.innerWidth || document.documentElement.clientWidth;
	const windowHeight =
		window.innerHeight || document.documentElement.clientHeight;

	// Get parent horizontal scroll container if it exists
	const horizontalScrollContainer = el.closest(".horizontal-scroll");
	let containerRect = null;
	let isInHorizontalView = true;

	if (horizontalScrollContainer) {
		containerRect = horizontalScrollContainer.getBoundingClientRect();
		// Check if element is within the horizontal scroll container's viewport
		isInHorizontalView =
			(rect.left >= containerRect.left - margin &&
				rect.left <= containerRect.right + margin) ||
			(rect.right >= containerRect.left - margin &&
				rect.right <= containerRect.right + margin);
	}

	// Add margin to viewport check
	const isVerticallyVisible =
		(rect.top >= -margin && rect.top <= windowHeight + margin) ||
		(rect.bottom >= -margin && rect.bottom <= windowHeight + margin);
	const isHorizontallyVisible =
		(rect.left >= -margin && rect.left <= windowWidth + margin) ||
		(rect.right >= -margin && rect.right <= windowWidth + margin);

	return isVerticallyVisible && (isHorizontallyVisible || isInHorizontalView);
}

//Draw SVG - Improved animation handling with horizontal scroll support
function handleSVGAnimation(element) {
	if (!element || !element.classList) return;

	try {
		if (isInViewport(element, 100)) {
			// Added 100px margin
			if (!element.classList.contains("startAnimate")) {
				element.classList.add("startAnimate");
				// Force reflow to ensure animation starts
				void element.offsetWidth;
			}
		} else {
			if (element.classList.contains("startAnimate")) {
				element.classList.remove("startAnimate");
			}
		}
	} catch (error) {
		console.warn("Error handling SVG animation:", error);
	}
}

// Initialize SVG animations for both scroll and slider scenarios
function initializeSVGAnimations() {
	const svgElements = document.querySelectorAll(".draw-svg");
	if (!svgElements.length) return;

	// Throttled scroll handler
	const throttledScrollHandler = throttle(() => {
		svgElements.forEach(handleSVGAnimation);
	}, 100); // 100ms throttle

	// Handle scroll events (both vertical and horizontal)
	window.addEventListener("scroll", throttledScrollHandler, { passive: true });

	// Handle horizontal scroll containers
	svgElements.forEach((element) => {
		const horizontalScrollContainer = element.closest(".horizontal-scroll");
		if (horizontalScrollContainer) {
			// Use both scroll and wheel events for better horizontal scroll detection
			horizontalScrollContainer.addEventListener(
				"scroll",
				throttle(() => handleSVGAnimation(element), 100),
				{ passive: true }
			);

			horizontalScrollContainer.addEventListener(
				"wheel",
				throttle(() => handleSVGAnimation(element), 100),
				{ passive: true }
			);
		}

		// Handle slider events
		const slider = element.closest(".splide");
		if (slider) {
			slider.addEventListener("move.splide", () => handleSVGAnimation(element));
		}
	});

	// Initial check for elements in viewport
	requestAnimationFrame(() => {
		svgElements.forEach(handleSVGAnimation);
	});
}
